import { Icon } from '@iconify/react';
import { Button } from '@/components/shadcn/button';
import { Card, CardDescription, CardTitle } from '@/components/shadcn/card';
import type { BadmintonRotationFormat, TambahGameFormState } from './badmintonGenerateUtils';
import { previewRandomRotationGames } from './badmintonGenerateUtils';
import BadmintonCompactStepper from './BadmintonCompactStepper';

interface TambahGameFormatFieldsProps {
  form: TambahGameFormState;
  confirmedCount: number;
  onChange: (patch: Partial<TambahGameFormState>) => void;
}

const FORMATS: {
  value: BadmintonRotationFormat;
  icon: string;
  title: string;
  hint: string;
}[] = [
  {
    value: 'random_rotation',
    icon: 'mdi:shuffle-variant',
    title: 'Rotasi Acak',
    hint: 'Pasangan diacak tiap laga, tiap orang main jumlah laga yang sama.',
  },
  {
    value: 'round_robin',
    icon: 'mdi:format-list-numbered',
    title: 'Round Robin',
    hint: 'Setiap pasangan bertemu semua pasangan lain sekali.',
  },
  {
    value: 'king_of_the_court',
    icon: 'mdi:crown-outline',
    title: 'King of the Court',
    hint: 'Pemenang tetap di lapangan, yang kalah bergantian keluar.',
  },
];

const cardClass = 'rounded-2xl border-0 bg-white p-4 shadow-[inset_0_0_0_1px_#E0E6ED]';

export default function TambahGameFormatFields({
  form,
  confirmedCount,
  onChange,
}: TambahGameFormatFieldsProps) {
  const perSide = form.playerFormat === 'singles' ? 1 : 2;
  const preview = previewRandomRotationGames(confirmedCount, perSide, form.numRounds);

  return (
    <div className="space-y-3">
      <Card className={cardClass}>
        <CardTitle className="text-sm font-extrabold text-[#0E1726]">Format Penyusunan</CardTitle>
        <CardDescription className="mt-1 text-xs text-gray-400">
          Cara jadwal laga dibuat dari daftar pemain.
        </CardDescription>
        <div className="mt-3 space-y-2">
          {FORMATS.map((opt) => {
            const selected = form.rotationFormat === opt.value;
            return (
              <Button
                key={opt.value}
                type="button"
                variant="outline"
                aria-pressed={selected}
                onClick={() => onChange({ rotationFormat: opt.value })}
                className={`h-auto w-full items-center justify-start gap-3 rounded-2xl px-3 py-3 text-left ${
                  selected ? 'border-[#FC480D] bg-[#FFF8F5]' : 'border-gray-200 bg-white'
                }`}
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-50 text-gray-500">
                  <Icon icon={opt.icon} className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-extrabold text-[#0E1726]">{opt.title}</span>
                  <span className="block break-words whitespace-normal text-xs font-normal text-gray-400">
                    {opt.hint}
                  </span>
                </span>
                {selected ? (
                  <Icon icon="mdi:check-circle" className="h-5 w-5 text-[#FC480D]" />
                ) : (
                  <span className="h-5 w-5 rounded-full border border-gray-200" />
                )}
              </Button>
            );
          })}
        </div>
      </Card>

      {form.rotationFormat === 'random_rotation' && (
        <Card className={cardClass}>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="text-sm font-extrabold text-[#0E1726]">
                Jumlah main per pemain
              </CardTitle>
              <CardDescription className="mt-1 text-xs text-gray-400">
                Akan menghasilkan {preview} pertandingan
              </CardDescription>
            </div>
            <BadmintonCompactStepper
              label="Jumlah main per pemain"
              value={form.numRounds}
              min={1}
              max={30}
              onChange={(numRounds) => onChange({ numRounds })}
            />
          </div>
        </Card>
      )}

      {form.rotationFormat === 'king_of_the_court' && (
        <div className="space-y-3">
          <Card className={`flex flex-row items-center justify-between gap-3 ${cardClass}`}>
            <div>
              <CardTitle className="text-sm font-extrabold text-[#0E1726]">
                Maksimal bertahan di lapangan
              </CardTitle>
              <CardDescription className="mt-1 text-xs text-gray-400">
                Kemenangan berurut sebelum wajib rotasi.
              </CardDescription>
            </div>
            <BadmintonCompactStepper
              label="Maksimal bertahan di lapangan"
              value={form.maxStayWins}
              min={1}
              max={30}
              onChange={(maxStayWins) => onChange({ maxStayWins })}
            />
          </Card>
        </div>
      )}
    </div>
  );
}
